import React, { useState, useEffect, useCallback } from 'react';
import { useI18n } from '../i18n';

const API_URL = process.env.REACT_APP_API_URL || '';

export default function GroupPanel({ currentUser, currentRoute, onLoadRoute, onClose }) {
  const { t } = useI18n();
  const [activeView, setActiveView] = useState('main'); // main, manage, routes
  const [pendingInvitations, setPendingInvitations] = useState([]);
  const [userGroups, setUserGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [groupRoutes, setGroupRoutes] = useState([]);
  const [message, setMessage] = useState(null);
  const [loading, setLoading] = useState(false);

  // Group management state (login with group credentials)
  const [groupName, setGroupName] = useState('');
  const [groupPassword, setGroupPassword] = useState('');
  const [managedGroup, setManagedGroup] = useState(null);
  const [newMemberUsername, setNewMemberUsername] = useState('');

  // Create group state
  const [showCreateGroup, setShowCreateGroup] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');
  const [newGroupPassword, setNewGroupPassword] = useState('');

  // Save route to group state
  const [showSaveRoute, setShowSaveRoute] = useState(false);
  const [saveRouteName, setSaveRouteName] = useState('');
  const [saveRouteGroupId, setSaveRouteGroupId] = useState(null);

  // Load user's pending invitations and groups
  const loadUserData = useCallback(async () => {
    if (!currentUser?.id) return;
    try {
      const [pendingRes, groupsRes] = await Promise.all([
        fetch(`${API_URL}/api/groups/user/${currentUser.id}/pending`),
        fetch(`${API_URL}/api/groups/user/${currentUser.id}/groups`)
      ]);
      const pendingData = await pendingRes.json();
      const groupsData = await groupsRes.json();
      if (pendingData.success) setPendingInvitations(pendingData.data || []);
      if (groupsData.success) setUserGroups(groupsData.data || []);
    } catch (err) {
      console.error('Error loading group data:', err);
    }
  }, [currentUser]);

  useEffect(() => { loadUserData(); }, [loadUserData]);

  // Accept invitation
  const acceptInvitation = async (memberId) => {
    try {
      const res = await fetch(`${API_URL}/api/groups/accept-invitation`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ memberId, userId: currentUser.id })
      });
      const data = await res.json();
      if (data.success) {
        setMessage({ type: 'success', text: t('group.invitationAccepted') });
        loadUserData();
      } else {
        setMessage({ type: 'error', text: data.message || 'Error' });
      }
    } catch { setMessage({ type: 'error', text: t('group.connectionError') }); }
  };

  // Reject invitation
  const rejectInvitation = async (memberId) => {
    try {
      const res = await fetch(`${API_URL}/api/groups/reject-invitation`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ memberId, userId: currentUser.id })
      });
      const data = await res.json();
      if (data.success) {
        setMessage({ type: 'success', text: t('group.invitationRejected') });
        loadUserData();
      } else {
        setMessage({ type: 'error', text: data.message || 'Error' });
      }
    } catch { setMessage({ type: 'error', text: t('group.connectionError') }); }
  };

  // Leave group
  const leaveGroup = async (groupId) => {
    if (!window.confirm(t('group.confirmLeave'))) return;
    try {
      const res = await fetch(`${API_URL}/api/groups/leave`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ groupId, userId: currentUser.id })
      });
      const data = await res.json();
      if (data.success) {
        setMessage({ type: 'success', text: t('group.leftGroup') });
        setSelectedGroup(null);
        loadUserData();
      } else {
        setMessage({ type: 'error', text: data.message || 'Error' });
      }
    } catch { setMessage({ type: 'error', text: t('group.connectionError') }); }
  };

  // Load group routes
  const loadGroupRoutes = async (groupId) => {
    try {
      const res = await fetch(`${API_URL}/api/groups/${groupId}/routes?userId=${currentUser.id}`);
      const data = await res.json();
      if (data.success) {
        setGroupRoutes(data.data || []);
        setSelectedGroup(userGroups.find(g => g.groupId === groupId) || { groupId });
        setActiveView('routes');
      } else {
        setMessage({ type: 'error', text: data.message || 'Error' });
      }
    } catch { setMessage({ type: 'error', text: t('group.connectionError') }); }
  };

  // Create group
  const handleCreateGroup = async () => {
    if (!newGroupName.trim() || !newGroupPassword.trim()) {
      setMessage({ type: 'error', text: t('group.namePasswordRequired') });
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/groups/create`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: newGroupName.trim(), password: newGroupPassword.trim() })
      });
      const data = await res.json();
      if (data.success) {
        setMessage({ type: 'success', text: t('group.created') });
        setShowCreateGroup(false);
        setNewGroupName('');
        setNewGroupPassword('');
      } else {
        setMessage({ type: 'error', text: data.message || 'Error' });
      }
    } catch { setMessage({ type: 'error', text: t('group.connectionError') }); }
    setLoading(false);
  };

  // Group management login
  const handleGroupLogin = async () => {
    if (!groupName.trim() || !groupPassword.trim()) {
      setMessage({ type: 'error', text: t('group.namePasswordRequired') });
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/groups/login`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: groupName.trim(), password: groupPassword.trim() })
      });
      const data = await res.json();
      if (data.success) {
        setManagedGroup(data.data);
        setActiveView('manage');
        setMessage(null);
      } else {
        setMessage({ type: 'error', text: data.message || 'Error' });
      }
    } catch { setMessage({ type: 'error', text: t('group.connectionError') }); }
    setLoading(false);
  };

  // Add member (group management)
  const handleAddMember = async () => {
    if (!newMemberUsername.trim()) {
      setMessage({ type: 'error', text: t('group.usernameRequired') });
      return;
    }
    try {
      const res = await fetch(`${API_URL}/api/groups/${managedGroup.id}/add-member`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: newMemberUsername.trim(), groupPassword: groupPassword.trim() })
      });
      const data = await res.json();
      if (data.success) {
        setMessage({ type: 'success', text: t('group.memberAdded') });
        setNewMemberUsername('');
        // Refresh group data
        handleGroupLogin();
      } else {
        setMessage({ type: 'error', text: data.message || 'Error' });
      }
    } catch { setMessage({ type: 'error', text: t('group.connectionError') }); }
  };

  // Remove member (group management)
  const handleRemoveMember = async (username) => {
    if (!window.confirm(t('group.confirmRemoveMember', { user: username }))) return;
    try {
      const res = await fetch(`${API_URL}/api/groups/${managedGroup.id}/remove-member`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, groupPassword: groupPassword.trim() })
      });
      const data = await res.json();
      if (data.success) {
        setMessage({ type: 'success', text: t('group.memberRemoved') });
        handleGroupLogin();
      } else {
        setMessage({ type: 'error', text: data.message || 'Error' });
      }
    } catch { setMessage({ type: 'error', text: t('group.connectionError') }); }
  };

  // Save route to group
  const handleSaveRouteToGroup = async () => {
    if (!saveRouteName.trim() || !saveRouteGroupId) {
      setMessage({ type: 'error', text: t('group.routeNameRequired') });
      return;
    }
    try {
      const res = await fetch(`${API_URL}/api/groups/${saveRouteGroupId}/routes`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: currentUser.id,
          name: saveRouteName.trim(),
          origin: currentRoute?.origin || '',
          destination: currentRoute?.destination || '',
          distance: currentRoute?.distance || 0,
          kmPoints: currentRoute?.kmPoints ? JSON.stringify(currentRoute.kmPoints) : null,
          maxDeviation: currentRoute?.maxDeviation || 0
        })
      });
      const data = await res.json();
      if (data.success) {
        setMessage({ type: 'success', text: t('group.routeSaved') });
        setShowSaveRoute(false);
        setSaveRouteName('');
      } else {
        setMessage({ type: 'error', text: data.message || 'Error' });
      }
    } catch { setMessage({ type: 'error', text: t('group.connectionError') }); }
  };

  const styles = {
    overlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.4)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' },
    panel: { background: '#fff', borderRadius: '12px', padding: '20px', maxWidth: '480px', width: '90%', maxHeight: '80vh', overflowY: 'auto', boxShadow: '0 8px 32px rgba(0,0,0,0.2)' },
    header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', borderBottom: '2px solid #6366f1', paddingBottom: '10px' },
    title: { margin: 0, fontSize: '1.2rem', color: '#4338ca' },
    closeBtn: { background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer', color: '#6b7280' },
    section: { marginBottom: '16px', padding: '12px', background: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' },
    sectionTitle: { margin: '0 0 8px', fontSize: '0.95rem', fontWeight: 600, color: '#1e293b' },
    btn: { padding: '8px 16px', borderRadius: '6px', border: 'none', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 500, transition: 'background 0.2s' },
    btnPrimary: { background: '#6366f1', color: '#fff' },
    btnSuccess: { background: '#10b981', color: '#fff' },
    btnDanger: { background: '#ef4444', color: '#fff' },
    btnSecondary: { background: '#6b7280', color: '#fff' },
    input: { width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.9rem', boxSizing: 'border-box', marginBottom: '8px' },
    invitationItem: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px', background: '#fef3c7', borderRadius: '6px', marginBottom: '6px' },
    groupItem: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px', background: '#dbeafe', borderRadius: '6px', marginBottom: '6px' },
    routeItem: { padding: '10px', background: '#f0fdf4', borderRadius: '6px', marginBottom: '6px', border: '1px solid #bbf7d0' },
    memberItem: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px', background: '#fff', borderRadius: '6px', marginBottom: '4px', border: '1px solid #e5e7eb' },
    msg: { padding: '8px 12px', borderRadius: '6px', fontSize: '0.85rem', marginBottom: '12px' },
    msgSuccess: { background: '#d1fae5', color: '#065f46' },
    msgError: { background: '#fee2e2', color: '#991b1b' },
  };

  return (
    <div style={styles.overlay} onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={styles.panel}>
        <div style={styles.header}>
          <h3 style={styles.title}>👥 {t('group.title')}</h3>
          <button style={styles.closeBtn} onClick={onClose}>✕</button>
        </div>

        {message && (
          <div style={{ ...styles.msg, ...(message.type === 'success' ? styles.msgSuccess : styles.msgError) }}>
            {message.text}
          </div>
        )}

        {/* MAIN VIEW: user's groups and invitations */}
        {activeView === 'main' && (
          <>
            {/* Pending invitations */}
            {pendingInvitations.length > 0 && (
              <div style={styles.section}>
                <h4 style={styles.sectionTitle}>🔔 {t('group.pendingInvitations')}</h4>
                {pendingInvitations.map(inv => (
                  <div key={inv.memberId} style={styles.invitationItem}>
                    <span style={{ fontWeight: 500 }}>{inv.groupName}</span>
                    <div style={{ display: 'flex', gap: '6px' }}>
                      <button style={{ ...styles.btn, ...styles.btnSuccess, padding: '4px 10px' }} onClick={() => acceptInvitation(inv.memberId)}>✓</button>
                      <button style={{ ...styles.btn, ...styles.btnDanger, padding: '4px 10px' }} onClick={() => rejectInvitation(inv.memberId)}>✗</button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* User's active groups */}
            <div style={styles.section}>
              <h4 style={styles.sectionTitle}>📁 {t('group.myGroups')}</h4>
              {userGroups.length === 0 ? (
                <p style={{ color: '#6b7280', fontSize: '0.85rem', margin: '8px 0' }}>{t('group.noGroups')}</p>
              ) : (
                userGroups.map(g => (
                  <div key={g.groupId} style={styles.groupItem}>
                    <span style={{ fontWeight: 500 }}>{g.groupName}</span>
                    <div style={{ display: 'flex', gap: '6px' }}>
                      <button style={{ ...styles.btn, ...styles.btnPrimary, padding: '4px 10px' }} onClick={() => loadGroupRoutes(g.groupId)}>📂</button>
                      <button style={{ ...styles.btn, ...styles.btnDanger, padding: '4px 10px' }} onClick={() => leaveGroup(g.groupId)}>🚪</button>
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Save route to group */}
            {currentRoute && userGroups.length > 0 && (
              <div style={styles.section}>
                <h4 style={styles.sectionTitle}>💾 {t('group.saveRouteToGroup')}</h4>
                {!showSaveRoute ? (
                  <button style={{ ...styles.btn, ...styles.btnSuccess }} onClick={() => setShowSaveRoute(true)}>
                    {t('group.saveCurrentRoute')}
                  </button>
                ) : (
                  <div>
                    <input type="text" value={saveRouteName} onChange={e => setSaveRouteName(e.target.value)}
                      placeholder={t('group.routeNamePlaceholder')} style={styles.input} />
                    <select value={saveRouteGroupId || ''} onChange={e => setSaveRouteGroupId(Number(e.target.value))}
                      style={{ ...styles.input, cursor: 'pointer' }}>
                      <option value="">{t('group.selectGroup')}</option>
                      {userGroups.map(g => <option key={g.groupId} value={g.groupId}>{g.groupName}</option>)}
                    </select>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button style={{ ...styles.btn, ...styles.btnSuccess }} onClick={handleSaveRouteToGroup}>{t('group.save')}</button>
                      <button style={{ ...styles.btn, ...styles.btnSecondary }} onClick={() => setShowSaveRoute(false)}>{t('group.cancel')}</button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Create group */}
            <div style={styles.section}>
              <h4 style={styles.sectionTitle}>➕ {t('group.createGroup')}</h4>
              {!showCreateGroup ? (
                <button style={{ ...styles.btn, ...styles.btnPrimary }} onClick={() => setShowCreateGroup(true)}>
                  {t('group.createNew')}
                </button>
              ) : (
                <div>
                  <input type="text" value={newGroupName} onChange={e => setNewGroupName(e.target.value)}
                    placeholder={t('group.groupNamePlaceholder')} style={styles.input} />
                  <input type="password" value={newGroupPassword} onChange={e => setNewGroupPassword(e.target.value)}
                    placeholder={t('group.groupPasswordPlaceholder')} style={styles.input} />
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button style={{ ...styles.btn, ...styles.btnSuccess }} disabled={loading} onClick={handleCreateGroup}>{t('group.create')}</button>
                    <button style={{ ...styles.btn, ...styles.btnSecondary }} onClick={() => setShowCreateGroup(false)}>{t('group.cancel')}</button>
                  </div>
                </div>
              )}
            </div>

            {/* Manage group (login with group credentials) */}
            <div style={styles.section}>
              <h4 style={styles.sectionTitle}>⚙️ {t('group.manageGroup')}</h4>
              <input type="text" value={groupName} onChange={e => setGroupName(e.target.value)}
                placeholder={t('group.groupNamePlaceholder')} style={styles.input} />
              <input type="password" value={groupPassword} onChange={e => setGroupPassword(e.target.value)}
                placeholder={t('group.groupPasswordPlaceholder')} style={styles.input} />
              <button style={{ ...styles.btn, ...styles.btnPrimary }} disabled={loading} onClick={handleGroupLogin}>
                {t('group.enterManagement')}
              </button>
            </div>
          </>
        )}

        {/* MANAGE VIEW: group admin (logged in with group credentials) */}
        {activeView === 'manage' && managedGroup && (
          <>
            <button style={{ ...styles.btn, ...styles.btnSecondary, marginBottom: '12px' }} onClick={() => { setActiveView('main'); setManagedGroup(null); }}>
              ← {t('group.back')}
            </button>
            <div style={styles.section}>
              <h4 style={styles.sectionTitle}>⚙️ {managedGroup.name} — {t('group.management')}</h4>
              <p style={{ fontSize: '0.85rem', color: '#6b7280' }}>{t('group.membersCount', { count: managedGroup.members?.length || 0 })}</p>

              {/* Members list */}
              {managedGroup.members && managedGroup.members.map(m => (
                <div key={m.id} style={styles.memberItem}>
                  <div>
                    <span style={{ fontWeight: 500 }}>{m.username}</span>
                    <span style={{ marginLeft: '8px', fontSize: '0.75rem', padding: '2px 6px', borderRadius: '4px', background: m.status === 'ACTIVE' ? '#d1fae5' : '#fef3c7', color: m.status === 'ACTIVE' ? '#065f46' : '#92400e' }}>
                      {m.status === 'ACTIVE' ? t('group.active') : t('group.pending')}
                    </span>
                  </div>
                  <button style={{ ...styles.btn, ...styles.btnDanger, padding: '4px 8px', fontSize: '0.8rem' }}
                    onClick={() => handleRemoveMember(m.username)}>✗</button>
                </div>
              ))}

              {/* Add member */}
              <div style={{ marginTop: '12px', display: 'flex', gap: '8px' }}>
                <input type="text" value={newMemberUsername} onChange={e => setNewMemberUsername(e.target.value)}
                  placeholder={t('group.usernamePlaceholder')} style={{ ...styles.input, marginBottom: 0, flex: 1 }} />
                <button style={{ ...styles.btn, ...styles.btnSuccess }} onClick={handleAddMember}>+</button>
              </div>
            </div>
          </>
        )}

        {/* ROUTES VIEW: view group routes */}
        {activeView === 'routes' && selectedGroup && (
          <>
            <button style={{ ...styles.btn, ...styles.btnSecondary, marginBottom: '12px' }} onClick={() => { setActiveView('main'); setSelectedGroup(null); }}>
              ← {t('group.back')}
            </button>
            <div style={styles.section}>
              <h4 style={styles.sectionTitle}>📂 {selectedGroup.groupName} — {t('group.routes')}</h4>
              {groupRoutes.length === 0 ? (
                <p style={{ color: '#6b7280', fontSize: '0.85rem' }}>{t('group.noRoutes')}</p>
              ) : (
                groupRoutes.map(r => (
                  <div key={r.id} style={styles.routeItem}>
                    <div style={{ fontWeight: 600, marginBottom: '4px' }}>{r.name}</div>
                    <div style={{ fontSize: '0.8rem', color: '#6b7280' }}>
                      {r.origin} → {r.destination} {r.distance ? `(${r.distance} km)` : ''}
                    </div>
                    <div style={{ fontSize: '0.75rem', color: '#9ca3af', marginTop: '2px' }}>
                      {t('group.savedBy')}: {r.savedBy} — {r.createdAt ? new Date(r.createdAt).toLocaleDateString() : ''}
                    </div>
                    {onLoadRoute && (
                      <button style={{ ...styles.btn, ...styles.btnPrimary, marginTop: '6px', padding: '4px 12px', fontSize: '0.8rem' }}
                        onClick={() => { onLoadRoute({ origin: r.origin, destination: r.destination, distance: r.distance, kmPoints: r.kmPoints ? JSON.parse(r.kmPoints) : [], maxDeviation: r.maxDeviation }); onClose(); }}>
                        {t('group.loadRoute')}
                      </button>
                    )}
                  </div>
                ))
              )}
              <button style={{ ...styles.btn, ...styles.btnDanger, marginTop: '8px' }} onClick={() => leaveGroup(selectedGroup.groupId)}>
                🚪 {t('group.leaveGroup')}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
